import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  apiGet,
  apiPost,
  apiPut,
  apiDelete,
} from "../../services/api/apiClient";
import type { Customer } from "../../types/api";

// Billing address entry returned by /get-customer-billing-addresses
export interface BillingAddressEntry {
  customerId: string;
  customerName: string;
  billAddress1: string;
  billAddress2?: string;
  billAddressCity: string;
  billAddressState: string;
  billAddressZip: string;
  billAddressCountry: string;
}

type LoadingStatus = "idle" | "loading" | "succeeded" | "failed";

interface CustomersState {
  customers: Customer[];
  status: LoadingStatus;
  error: string | null;
  customerId: string;
  changes: Partial<Customer> | null;
  selectedCustomer: Customer | null;
  drawerOpen: boolean;
  fetchCustomerStatus: LoadingStatus;
  fetchCustomerError: string | null;
  createCustomerStatus: LoadingStatus;
  createCustomerError: string | null;
  updateCustomerStatus: LoadingStatus;
  updateCustomerError: string | null;
  deleteCustomerStatus: LoadingStatus;
  deleteCustomerError: string | null;
  deleteConfirmationOpen: boolean;
  customerToDelete: string | null;
  meetYourTeamHeader: string;
  meetYourTeamTextBox: string;
  backgroundUploading: boolean;
  backgroundDeleting: boolean;
  backgroundError: string | null;
  backgroundDeleteError: string | null;
  backgroundSuccess: boolean;
  backgroundDeleteSuccess: boolean;
  filters: {
    status: string[];
    role?: string[];
    createdFrom: string;
    createdTo: string;
  };
  syncStatus: LoadingStatus;
  syncError: string | null;
  // What the sync answered, so the page can say what happened instead of only
  // that the button stopped spinning.
  syncMessage: string | null;
  billingAddresses: BillingAddressEntry[];
  billingAddressesStatus: LoadingStatus;
  billingAddressesError: string | null;
}

// Define the initial state
const initialState: CustomersState = {
  customers: [],
  status: "idle",
  error: null,

  customerId: "",
  changes: null as any,
  selectedCustomer: null as any,
  drawerOpen: false,

  fetchCustomerStatus: "idle",
  fetchCustomerError: null,

  createCustomerStatus: "idle",
  createCustomerError: null,

  updateCustomerStatus: "idle",
  updateCustomerError: null,

  deleteCustomerStatus: "idle",
  deleteCustomerError: null,
  deleteConfirmationOpen: false,
  customerToDelete: null,

  meetYourTeamHeader: "",
  meetYourTeamTextBox: "",

  // Background upload/delete states
  backgroundUploading: false,
  backgroundDeleting: false,
  backgroundError: null,
  backgroundDeleteError: null,
  backgroundSuccess: false,
  backgroundDeleteSuccess: false,

  filters: {
    status: ["active"],
    createdFrom: "",
    createdTo: "",
  },
  syncStatus: "idle",
  syncError: null,
  syncMessage: null,

  billingAddresses: [],
  billingAddressesStatus: "idle",
  billingAddressesError: null,
};

// Define an async thunk for fetching customers
export const fetchCustomers = createAsyncThunk(
  "customers/fetchCustomers",
  async (_, { getState, rejectWithValue }) => {
    // Which accounting system to list comes from the switch in the navbar, so
    // the list does not need to know about it; the API falls back to the same
    // global setting when the parameter is absent.
    const source = (getState() as any)?.source?.source;
    try {
      const data = await apiGet({
        endpoint: "/get-customers-by-filters",
        params: source ? { source } : undefined,
        withAuth: true,
      });
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch customers");
    }
  },
);

export const fetchCustomerById = createAsyncThunk(
  "customers/fetchCustomerById",
  async (id: any, { rejectWithValue }) => {
    // Token check is handled by apiClient with withAuth: true
    try {
      const data = await apiGet({
        endpoint: `/get-customer`,
        params: { id },
        withAuth: true,
      });
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch customer");
    }
  },
);

export const fetchCustomersByUserId = createAsyncThunk(
  "customers/fetchCustomersByUserId",
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as any;
    const userId = state.login.user?.id;
    // Token check is handled by apiClient with withAuth: true
    try {
      const data = await apiGet({
        endpoint: "/get-customers-by-user-id",
        params: { userId },
        withAuth: true,
      });
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch customers");
    }
  },
);

export const createCustomer = createAsyncThunk(
  "customers/createCustomer",
  async (customer: Record<string, unknown>, { rejectWithValue }) => {
    // Token check is handled by apiClient with withAuth: true
    try {
      const data = await apiPost({
        endpoint: "/create-customer",
        data: customer,
        withAuth: true,
      });
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to create customer");
    }
  },
);

export const updateCustomer = createAsyncThunk(
  "customers/updateCustomer",
  async ({ id, ...customer }: any, { rejectWithValue, dispatch }) => {
    // Token check is handled by apiClient with withAuth: true
    try {
      const data = await apiPut({
        endpoint: `/update-customer`,
        data: customer,
        params: { id },
        withAuth: true,
      });

      // If the response doesn't include the updated customer data, refetch it
      if (!data || !data.team) {
        dispatch(fetchCustomerById(id));
      }

      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to update customer");
    }
  },
);

export const deleteCustomer = createAsyncThunk(
  "customers/deleteCustomer",
  async (id: any, { rejectWithValue }) => {
    // Token check is handled by apiClient with withAuth: true
    try {
      const data = await apiDelete({
        endpoint: `/delete-customer`,
        params: { id }, // Assuming id is a query param for DELETE
        withAuth: true,
        // If DELETE expects a body for the id, it should be under 'data': { id }
      });
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to delete customer");
    }
  },
);

export const updateCustomerBackground = createAsyncThunk(
  "customers/updateCustomerBackground",
  async ({ imageFile, customerId }: any, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("backgroundImage", imageFile);

      const response = await apiPost({
        endpoint: "/upload-customer-background",
        params: { id: customerId },
        data: formData,
        withAuth: true,
      });

      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  },
);

export const deleteCustomerBackground = createAsyncThunk(
  "customers/deleteCustomerBackground",
  async (customerId, { rejectWithValue }) => {
    try {
      const response = await apiDelete({
        endpoint: "/delete-customer-background",
        params: { id: customerId },
        withAuth: true,
      });

      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  },
);

// Fetch billing addresses for all customers from Bill.com
export const fetchBillingAddresses = createAsyncThunk(
  "customers/fetchBillingAddresses",
  async (_, { rejectWithValue }) => {
    try {
      const data = await apiGet({
        endpoint: "/get-customer-billing-addresses",
        withAuth: true,
      });
      return data as BillingAddressEntry[];
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch billing addresses");
    }
  },
);

export interface FixCountryCodesResult {
  total: number;
  fixed: number;
  skipped: number;
  errors: number;
}

export const fixCountryCodes = createAsyncThunk(
  "customers/fixCountryCodes",
  async (_, { rejectWithValue }) => {
    try {
      const data = await apiPost({
        endpoint: "/fix-customer-country-codes",
        data: {},
        withAuth: true,
      });
      return data as FixCountryCodesResult;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fix country codes");
    }
  },
);

// Manual Bill.com customers sync
export const runCustomersSync = createAsyncThunk(
  "customers/runCustomersSync",
  async (_, { rejectWithValue }) => {
    try {
      const data = await apiPost({
        endpoint: "/run-customers-sync",
        data: {},
        withAuth: true,
      });
      return data;
    } catch (error: any) {
      return rejectWithValue(
        error.message || "Failed to run customers sync",
      );
    }
  },
);

// Create the customers slice
const customersSlice = createSlice({
  name: "customers",
  initialState,
  reducers: {
    // Define any synchronous actions here if needed
    openDrawer: (state) => {
      state.drawerOpen = true;
      state.deleteCustomerStatus = "idle";
      state.deleteCustomerError = null;
    },
    closeDrawer: (state) => {
      state.drawerOpen = false;
      state.selectedCustomer = null;
      state.changes = null;
      state.customerId = "";
      state.fetchCustomerStatus = "idle";
      state.fetchCustomerError = null;
      state.createCustomerStatus = "idle";
      state.createCustomerError = null;
      state.updateCustomerStatus = "idle";
      state.updateCustomerError = null;
      state.deleteCustomerStatus = "idle";
      state.deleteCustomerError = null;
    },
    addCustomer: (state) => {
      state.customerId = "";
      state.selectedCustomer = null;
      state.changes = null;
      state.drawerOpen = true;
      state.fetchCustomerStatus = "idle";
      state.fetchCustomerError = null;
      state.createCustomerStatus = "idle";
      state.createCustomerError = null;
      state.updateCustomerStatus = "idle";
      state.updateCustomerError = null;
      state.deleteCustomerStatus = "idle";
      state.deleteCustomerError = null;
    },
    selectCustomer: (state, action) => {
      state.customerId = action.payload;
      state.drawerOpen = true;
      state.fetchCustomerStatus = "idle";
      state.fetchCustomerError = null;
      state.createCustomerStatus = "idle";
      state.createCustomerError = null;
      state.updateCustomerStatus = "idle";
      state.updateCustomerError = null;
      state.deleteCustomerStatus = "idle";
      state.deleteCustomerError = null;
    },
    handleCustomerChange: (state, action) => {
      state.changes = {
        ...state.changes,
        [action.payload.name]: action.payload.value,
      };
    },
    handleBusinessChange: (state, action) => {
      state.changes = {
        ...state.changes,
        doingBusinessWith: action.payload,
      };
    },
    handleAuthorizedChange: (state, action) => {
      state.changes = {
        ...state.changes,
        authorizedUsers: action.payload,
      };
    },

    setMeetYourTeamHeader: (state, action) => {
      state.changes = {
        ...state.changes,
        meetYourTeamHeader: action.payload,
      };
    }, // meetYourTeamHeader

    setMeetYourTeamTextBox: (state, action) => {
      state.changes = {
        ...state.changes,
        meetYourTeamTextBox: action.payload,
      };
    }, // meetYourTeamTextBox

    handleTeamChange: (state, action) => {
      // Convert full user objects to just IDs for storage optimization
      const teamData = action.payload;
      const optimizedTeam: any = {};

      // Extract only user IDs from each category
      Object.keys(teamData).forEach((category) => {
        optimizedTeam[category] = teamData[category].map((user: any) =>
          typeof user === "object" && user.id ? user.id : user,
        );
      });

      state.changes = {
        ...state.changes,
        team: optimizedTeam,
      };
    },
    addTeamCategory: (state, action) => {
      const { categoryName } = action.payload;
      const currentTeam =
        state.changes?.team || state.selectedCustomer?.team || {};

      state.changes = {
        ...state.changes,
        team: {
          ...currentTeam,
          [categoryName]: [],
        },
      };
    },
    renameTeamCategory: (state, action) => {
      const { oldName, newName } = action.payload;
      const currentTeam =
        state.changes?.team || state.selectedCustomer?.team || {};

      if (currentTeam[oldName]) {
        const { [oldName]: categoryUsers, ...restCategories } = currentTeam;
        state.changes = {
          ...state.changes,
          team: {
            ...restCategories,
            [newName]: categoryUsers,
          },
        };
      }
    },
    deleteTeamCategory: (state, action) => {
      const { categoryName } = action.payload;
      const currentTeam =
        state.changes?.team || state.selectedCustomer?.team || {};

      const { [categoryName]: _, ...restCategories } = currentTeam;
      state.changes = {
        ...state.changes,
        team: restCategories,
      };
    },
    closeDeleteConfirmation: (state) => {
      state.deleteConfirmationOpen = false;
      state.customerToDelete = null;
    },
    changeFilter: (state, action) => {
      (state.filters as any)[action.payload.name] = action.payload.value;
    },
    clearFilters: (state) => {
      state.filters = {
        role: [],
        status: ["active"],
        createdFrom: "",
        createdTo: "",
        // company: [],
      };
    },
    handleClearStatus: (state) => {
      state.status = "idle";
      state.error = null;
    },
    clearSyncStatus: (state) => {
      state.syncStatus = "idle";
      state.syncError = null;
    },
    resetBackgroundErrors: (state) => {
      state.backgroundError = null;
      state.backgroundDeleteError = null;
    },
    resetBackgroundSuccess: (state) => {
      state.backgroundSuccess = false;
      state.backgroundDeleteSuccess = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCustomers.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchCustomers.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.customers = action.payload;
      })
      .addCase(fetchCustomers.rejected, (state, action) => {
        state.status = "failed";
        state.error = (action.payload as string) ?? action.error?.message ?? null;
      })
      .addCase(fetchCustomersByUserId.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchCustomersByUserId.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.customers = action.payload;
      })
      .addCase(fetchCustomersByUserId.rejected, (state, action) => {
        state.status = "failed";
        state.error = (action.payload as string) ?? action.error?.message ?? null;
      })
      .addCase(fetchCustomerById.pending, (state) => {
        state.fetchCustomerStatus = "loading";
      })
      .addCase(fetchCustomerById.fulfilled, (state, action) => {
        state.fetchCustomerStatus = "succeeded";

        // Handle team data - ensure it's in the correct format (IDs only)
        let teamData = action.payload?.team || {
          Accounting: [],
          Consulting: [],
          Finances: [],
        };

        // If team data contains full user objects, convert to IDs
        const optimizedTeam: any = {};
        Object.keys(teamData).forEach((category) => {
          optimizedTeam[category] = (teamData[category] || []).map((user: any) =>
            typeof user === "object" && user.id ? user.id : user,
          );
        });

        state.selectedCustomer = {
          id: action.payload?.id || "",
          email: action.payload?.email || "",
          name: action.payload?.name || "",
          timeCreated: action.payload?.timeCreated || "",
          timeUpdated: action.payload?.timeUpdated || "",
          // lastName: action.payload?.name?.Last || "",
          bankAccount: action.payload?.bankAccount || "",
          doingBusinessWith: action.payload?.doingBusinessWith || [],
          phone: action.payload?.phone || "",
          events: action.payload?.events || [],
          status: action.payload?.status
            ? action.payload?.status?.toLowerCase()
            : "active",
          authorizedUsers: action.payload?.authorizedUsers || [],
          backgroundImage: action.payload?.backgroundImage || null,
          team: optimizedTeam,
        };
        state.meetYourTeamHeader = action.payload?.meetYourTeamHeader || "";
        state.meetYourTeamTextBox = action.payload?.meetYourTeamTextBox || "";
      })
      .addCase(fetchCustomerById.rejected, (state, action) => {
        state.fetchCustomerStatus = "failed";
        state.fetchCustomerError = (action.payload as string) ?? action.error?.message ?? null;
      })
      .addCase(createCustomer.pending, (state) => {
        state.createCustomerStatus = "loading";
      })
      .addCase(createCustomer.fulfilled, (state) => {
        state.createCustomerStatus = "succeeded";
        state.changes = null;
        state.status = "idle";
      })
      .addCase(createCustomer.rejected, (state, action) => {
        state.createCustomerStatus = "failed";
        state.createCustomerError = (action.payload as string) ?? action.error?.message ?? null;
      })
      .addCase(updateCustomer.pending, (state) => {
        state.updateCustomerStatus = "loading";
      })
      .addCase(updateCustomer.fulfilled, (state, action) => {
        state.updateCustomerStatus = "succeeded";

        // Update the selectedCustomer with the saved data
        if (state.selectedCustomer && action.payload) {
          // Handle team data - ensure it's in the correct format (IDs only)
          let teamData = action.payload?.team || state.selectedCustomer.team;
          const optimizedTeam: any = {};
          Object.keys(teamData).forEach((category) => {
            optimizedTeam[category] = (teamData[category] || []).map((user: any) =>
              typeof user === "object" && user.id ? user.id : user,
            );
          });

          state.selectedCustomer = {
            ...state.selectedCustomer,
            ...action.payload,
            team: optimizedTeam,
          };
          state.meetYourTeamHeader = action.payload?.meetYourTeamHeader || "";
          state.meetYourTeamTextBox = action.payload?.meetYourTeamTextBox || "";
        } else {
          // If no response data, preserve the current changes in selectedCustomer
          if (state.changes && state.selectedCustomer) {
            state.selectedCustomer = {
              ...state.selectedCustomer,
              ...state.changes,
            };
            state.meetYourTeamHeader = action.payload?.meetYourTeamHeader || "";
            state.meetYourTeamTextBox =
              action.payload?.meetYourTeamTextBox || "";
          }
        }

        state.changes = null;
        state.status = "idle";
      })
      .addCase(updateCustomer.rejected, (state, action) => {
        state.updateCustomerStatus = "failed";
        state.updateCustomerError = (action.payload as string) ?? action.error?.message ?? null;
      })
      .addCase(deleteCustomer.pending, (state) => {
        state.deleteCustomerStatus = "loading";
      })
      .addCase(deleteCustomer.fulfilled, (state) => {
        state.deleteCustomerStatus = "succeeded";
        state.status = "idle";
      })
      .addCase(deleteCustomer.rejected, (state, action) => {
        state.deleteCustomerStatus = "failed";
        state.deleteCustomerError = (action.payload as string) ?? action.error?.message ?? null;
      })
      // Upload customer background
      .addCase(updateCustomerBackground.pending, (state) => {
        state.backgroundUploading = true;
        state.backgroundError = null;
        state.backgroundSuccess = false;
      })
      .addCase(updateCustomerBackground.fulfilled, (state) => {
        state.backgroundUploading = false;
        state.backgroundSuccess = true;
      })
      .addCase(updateCustomerBackground.rejected, (state, action) => {
        state.backgroundUploading = false;
        state.backgroundError = action.payload as string;
      })
      // Delete customer background
      .addCase(deleteCustomerBackground.pending, (state) => {
        state.backgroundDeleting = true;
        state.backgroundDeleteError = null;
        state.backgroundDeleteSuccess = false;
      })
      .addCase(deleteCustomerBackground.fulfilled, (state) => {
        state.backgroundDeleting = false;
        state.backgroundDeleteSuccess = true;
      })
      .addCase(deleteCustomerBackground.rejected, (state, action) => {
        state.backgroundDeleting = false;
        state.backgroundDeleteError = action.payload as string;
      })
      // Manual customers sync
      .addCase(runCustomersSync.pending, (state) => {
        state.syncStatus = "loading";
        state.syncError = null;
        state.syncMessage = null;
      })
      .addCase(runCustomersSync.fulfilled, (state, action: any) => {
        state.syncStatus = "succeeded";
        state.syncError = null;
        state.syncMessage = action.payload?.message || "Sync finished";
        // Refresh customers list after sync
        state.status = "idle";
      })
      .addCase(runCustomersSync.rejected, (state, action) => {
        state.syncStatus = "failed";
        state.syncError = action.payload as string;
      })
      // Billing addresses from Bill.com
      .addCase(fetchBillingAddresses.pending, (state) => {
        state.billingAddressesStatus = "loading";
        state.billingAddressesError = null;
      })
      .addCase(fetchBillingAddresses.fulfilled, (state, action) => {
        state.billingAddressesStatus = "succeeded";
        state.billingAddresses = action.payload;
      })
      .addCase(fetchBillingAddresses.rejected, (state, action) => {
        state.billingAddressesStatus = "failed";
        state.billingAddressesError = action.payload as string;
      })
      .addCase(fixCountryCodes.pending, (_state) => {
        // no separate loading state needed, handled inline
      })
      .addCase(fixCountryCodes.fulfilled, (state) => {
        // Refetch billing addresses after fix
        state.billingAddressesStatus = "idle";
      })
      .addCase(fixCountryCodes.rejected, (_state) => {
        // no separate error state needed
      });
  },
});

// Export the actions
export const {
  selectCustomer,
  openDrawer,
  closeDrawer,
  addCustomer,
  handleCustomerChange,
  handleBusinessChange,
  handleAuthorizedChange,
  handleTeamChange,
  setMeetYourTeamHeader,
  setMeetYourTeamTextBox,

  addTeamCategory,
  renameTeamCategory,
  deleteTeamCategory,
  changeFilter,
  clearFilters,
  handleClearStatus,
  resetBackgroundErrors,
  resetBackgroundSuccess,
  closeDeleteConfirmation,
  clearSyncStatus,
} = customersSlice.actions;

// Selector to map team IDs back to full user objects
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const selectTeamWithUserData = (state: any) => {
  const { selectedCustomer, changes } = state.customers;
  const { users: usersArray = [] } = state.users;

  // Get team data (IDs only) from Redux - prioritize changes over selectedCustomer
  const teamIds = changes?.team ||
    selectedCustomer?.team || {
      Accounting: [],
      Finances: [],
      Consulting: [],
    };

  // Create a user lookup map for efficiency
  const userLookup = usersArray.reduce((acc: any, user: any) => {
    acc[user.id] = user;
    return acc;
  }, {});

  // Map IDs back to full user objects
  const teamData: any = {};
  Object.keys(teamIds).forEach((category) => {
    teamData[category] = (teamIds[category] || [])
      .map((userId: any) => {
        // Handle both ID strings/numbers and already-mapped user objects
        if (typeof userId === "object" && userId.id) {
          return userId; // Already a user object
        }
        return userLookup[userId]; // Map ID to user object
      })
      .filter(Boolean); // Remove any users that couldn't be found
  });

  return teamData;
};

// Export the async thunk and the reducer
export default customersSlice.reducer;
